import { useState } from 'react';
import { sourceForChannel } from '../util/channel-source.ts';

// Search-result card for `search_video_moments`. Groups returned chunks by
// videoId — one row per video, even when the search returns many timestamps
// from the same upload. Shows thumbnail + channel/title/topic identity +
// a row of clickable timestamp pills. Quotes were removed (2026-05-21):
// the 30s chunks are too noisy out-of-context and burying the actual
// mention; sending the user straight to the YouTube timestamp is cleaner.

type VideoMoment = {
  chunk_id: number;
  start_sec: number;
  end_sec: number;
  timestamp_mmss: string;
  channel_name: string | null;
  video_title: string | null;
  published_at: string | null;
  topic_label: string | null;
  pokemon_referenced: string[] | null;
  source_url: string | null;
  quote: string;
};

export type VideoMomentsCardData = {
  ok: true;
  count: number;
  results: VideoMoment[];
};

function videoIdFromUrl(url: string | null): string | null {
  if (!url) return null;
  const qp = url.match(/[?&]v=([A-Za-z0-9_-]{11})/);
  if (qp) return qp[1];
  const bare = url.match(/(^|[/=])([A-Za-z0-9_-]{11})(?:$|[&?#])/);
  return bare ? bare[2] : null;
}

function shortDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function videoUrlAt(videoId: string, sec: number): string {
  return `https://www.youtube.com/watch?v=${videoId}&t=${Math.max(0, Math.floor(sec))}`;
}

type Grouped = {
  videoId: string;
  channelName: string | null;
  videoTitle: string | null;
  publishedAt: string | null;
  primary: VideoMoment;
  others: VideoMoment[];
};

function group(results: VideoMoment[]): Grouped[] {
  const map = new Map<string, Grouped>();
  for (const r of results) {
    const vid = videoIdFromUrl(r.source_url);
    if (!vid) continue; // skip rows we can't identify
    const existing = map.get(vid);
    if (!existing) {
      map.set(vid, {
        videoId: vid,
        channelName: r.channel_name,
        videoTitle: r.video_title,
        publishedAt: r.published_at,
        primary: r,
        others: [],
      });
    } else {
      existing.others.push(r);
    }
  }
  return [...map.values()];
}

export function VideoMomentsCard({ data }: { data: VideoMomentsCardData }) {
  const grouped = group(data?.results ?? []);
  if (grouped.length === 0) {
    return (
      <div
        className="mono"
        style={{
          padding: '8px 12px',
          color: 'var(--fg-3)',
          fontSize: 12,
          border: '1px dashed var(--line-1)',
          borderRadius: 4,
        }}
      >
        no video moments matched
      </div>
    );
  }
  const totalChunks = grouped.reduce((n, g) => n + 1 + g.others.length, 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        className="caps mono"
        style={{ fontSize: 9.5, color: 'var(--fg-4)', letterSpacing: '0.08em' }}
      >
        creator moments · {grouped.length} {grouped.length === 1 ? 'video' : 'videos'}
        {totalChunks > grouped.length && ` · ${totalChunks} timestamps`}
      </div>
      {grouped.map((g) => (
        <VideoMomentRow key={g.videoId} g={g} />
      ))}
    </div>
  );
}

function VideoMomentRow({ g }: { g: Grouped }) {
  const thumb = `https://i.ytimg.com/vi/${g.videoId}/mqdefault.jpg`;
  const primaryUrl = videoUrlAt(g.videoId, g.primary.start_sec);
  const [thumbErr, setThumbErr] = useState(false);
  const source = sourceForChannel(g.channelName);
  return (
    // Stacked layout: compact identity header on top, full-width quotes
    // below. The previous design put a 160px thumbnail beside the quote
    // column, which on a 360px mobile viewport left the quote ~200px wide —
    // every quote wrapped to 8+ ragged lines.
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 10,
        background: 'var(--bg-1)',
        border: '1px solid var(--line-1)',
        borderRadius: 4,
      }}
    >
      {/* Header row: small thumbnail + channel/date/title/topic. Thumbnail
          stays as the video identity marker but doesn't compete for width
          with the quote text. */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <a
          href={primaryUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            position: 'relative',
            flex: '0 0 120px',
            width: 120,
            aspectRatio: '16 / 9',
            background: 'var(--bg-2)',
            borderRadius: 3,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textDecoration: 'none',
          }}
        >
          {!thumbErr ? (
            <img
              src={thumb}
              alt=""
              width={120}
              height={68}
              referrerPolicy="no-referrer"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              loading="lazy"
              onError={(e) => {
                const img = e.currentTarget;
                const alt = `https://i.ytimg.com/vi/${g.videoId}/hqdefault.jpg`;
                if (img.src !== alt) {
                  img.src = alt;
                } else {
                  setThumbErr(true);
                }
              }}
            />
          ) : (
            <span className="caps mono" style={{ color: 'var(--fg-4)', fontSize: 9 }}>
              thumb n/a
            </span>
          )}
          <span
            className="mono"
            style={{
              position: 'absolute',
              right: 3,
              bottom: 3,
              padding: '1px 4px',
              background: 'rgba(0, 0, 0, 0.85)',
              color: '#fff',
              fontSize: 9,
              borderRadius: 2,
            }}
          >
            {g.primary.timestamp_mmss}
          </span>
        </a>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 6,
              flexWrap: 'wrap',
              fontSize: 11.5,
              color: 'var(--fg-3)',
            }}
          >
            <span style={{ color: 'var(--fg-1)', fontWeight: 600 }}>{g.channelName}</span>
            {g.publishedAt && (
              <span style={{ color: 'var(--fg-4)' }}>{shortDate(g.publishedAt)}</span>
            )}
            {source.endsWith('-jp') && (
              <span
                className="caps mono"
                style={{
                  padding: '0 4px',
                  background: 'var(--bg-2)',
                  border: '1px solid var(--line-1)',
                  color: 'var(--fg-2)',
                  borderRadius: 2,
                  fontSize: 8.5,
                  letterSpacing: '0.1em',
                }}
              >
                JA
              </span>
            )}
          </div>
          {g.videoTitle && (
            <a
              href={primaryUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 12.5,
                color: 'var(--fg-2)',
                textDecoration: 'none',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {g.videoTitle}
            </a>
          )}
          {g.primary.topic_label && (
            <span
              className="caps mono"
              style={{
                alignSelf: 'flex-start',
                fontSize: 9,
                color: 'var(--fg-3)',
                padding: '0 5px',
                border: '1px solid var(--line-1)',
                borderRadius: 2,
                letterSpacing: '0.06em',
              }}
            >
              {g.primary.topic_label.replace(/_/g, ' ')}
            </span>
          )}
        </div>
      </div>

      {/* All timestamps as clickable pills. Primary is already on the
          thumbnail badge but also gets a pill here so every mention is
          jumpable from one row. */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          flexWrap: 'wrap',
          alignItems: 'baseline',
        }}
      >
        <span
          className="caps mono"
          style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.06em' }}
        >
          jump to
        </span>
        {[g.primary, ...g.others].map((o) => (
          <a
            key={o.chunk_id}
            href={videoUrlAt(g.videoId, o.start_sec)}
            target="_blank"
            rel="noopener noreferrer"
            className="mono"
            style={{
              fontSize: 10.5,
              color: 'var(--accent-2)',
              textDecoration: 'none',
              padding: '0 5px',
              border: '1px solid var(--accent-line)',
              borderRadius: 2,
              background: 'var(--accent-bg)',
            }}
          >
            {o.timestamp_mmss}
          </a>
        ))}
      </div>
      {g.primary.pokemon_referenced && g.primary.pokemon_referenced.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {g.primary.pokemon_referenced.slice(0, 8).map((p) => (
            <span
              key={p}
              className="mono"
              style={{
                fontSize: 10,
                color: 'var(--fg-4)',
                padding: '0 4px',
                background: 'var(--bg-2)',
                borderRadius: 2,
              }}
            >
              {p}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
